import { createFileRoute } from "@tanstack/react-router";
import { ContentPage, ContentSection, BiText } from "@/components/layout/ContentPage";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  head: () => {
    const seo = buildSeo({
      title: "Terms of Use | Bangla Calculator",
      description:
        "Terms of use for Bangla Calculator. Disclaimers for financial, health, tax, and Islamic calculators. Educational tools, not religious or legal rulings.",
      path: "/terms",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Terms", path: "/terms" },
      ],
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: TermsPage,
});

function TermsPage() {
  return (
    <ContentPage
      titleEn="Terms of Use"
      titleBn="ব্যবহারের শর্তাবলী"
      introEn="By using Bangla Calculator, you agree to the terms below. The calculators are educational tools — not professional, medical, legal, financial, or religious advice."
      introBn="বাংলা ক্যালকুলেটর ব্যবহার করে আপনি নিচের শর্তাবলীতে সম্মত হচ্ছেন। ক্যালকুলেটরগুলো শিক্ষাগত টুল — পেশাদার, চিকিৎসা, আইনি, আর্থিক বা ধর্মীয় পরামর্শ নয়।"
    >
      <ContentSection headingEn="1. Acceptance of terms" headingBn="১. শর্তাবলী গ্রহণ">
        <BiText
          en={<p>By accessing or using calculatorbangla.com, you agree to be bound by these terms. If you disagree, please discontinue use.</p>}
          bn={<p>calculatorbangla.com ব্যবহারের মাধ্যমে আপনি এই শর্তাবলীতে আবদ্ধ হতে সম্মত হচ্ছেন। অসম্মত হলে অনুগ্রহ করে ব্যবহার বন্ধ করুন।</p>}
        />
      </ContentSection>

      <ContentSection headingEn="2. Use of calculators" headingBn="২. ক্যালকুলেটর ব্যবহার">
        <BiText
          en={
            <>
              <p>All calculators are provided "as is" for general informational and educational purposes only.</p>
              <p>
                <strong>Not a substitute for professional advice:</strong> financial calculations are estimates, not financial advice;
                health calculators are not medical advice; tax calculators are not legal or tax advice;{" "}
                <strong>Islamic calculators are educational tools, not religious rulings (fatwa)</strong>.
              </p>
              <p>You are responsible for verifying outputs before making any decisions.</p>
            </>
          }
          bn={
            <>
              <p>সব ক্যালকুলেটর "যেমন আছে তেমন" ভিত্তিতে শুধু সাধারণ তথ্য ও শিক্ষাগত উদ্দেশ্যে দেওয়া হয়েছে।</p>
              <p>
                <strong>পেশাদার পরামর্শের বিকল্প নয়:</strong> আর্থিক হিসাব শুধু আনুমানিক, আর্থিক পরামর্শ নয়; স্বাস্থ্য ক্যালকুলেটর চিকিৎসা পরামর্শ নয়;
                কর ক্যালকুলেটর আইনি বা কর পরামর্শ নয়; <strong>ইসলামিক ক্যালকুলেটর শিক্ষাগত টুল, ফাতওয়া নয়</strong>।
              </p>
              <p>সিদ্ধান্ত নেওয়ার আগে ফলাফল যাচাই করার দায়িত্ব আপনার।</p>
            </>
          }
        />
      </ContentSection>

      <ContentSection headingEn="3. Calculator-specific disclaimers" headingBn="৩. ক্যালকুলেটর-ভিত্তিক দাবিত্যাগ">
        <BiText
          en={
            <>
              <h3 className="mt-2 font-semibold text-foreground">Financial</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>Tax calculator: rates change with each Finance Act; verify with NBR.</li>
                <li>EMI/Loan bank rates: indicative; verify with the bank.</li>
                <li>Currency converter: mid-market rates, not real transaction rates.</li>
                <li>Customs duty: depends on HS code, FTA, and customs assessment.</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">Land</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>Land calculator: regional variations exist; verify with mouza records.</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">Health</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>Consult medical professionals before acting on health outputs.</li>
                <li>Body fat estimation has ±3% error; calorie targets are estimates.</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">Educational</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>Math solver is for learning only — not for exam use.</li>
                <li>Casio emulators are practice tools; physical calculators are required for actual exams.</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">Islamic</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li><strong>Zakat:</strong> For estimation. Gold/silver prices fluctuate — verify with current market rates. Consult a qualified scholar for complex scenarios (business, mixed assets, agricultural Ushr, livestock).</li>
                <li><strong>Prayer Times:</strong> Astronomical calculation (Karachi method default). Local mosque adhan times may differ. Verify with your local mosque or Islamic Foundation Bangladesh for ceremonial precision.</li>
                <li><strong>Sehri &amp; Iftar:</strong> Times depend on calculation method and city. Always end Sehri before adhan and break fast at the Maghrib adhan.</li>
                <li><strong>Hijri Date Converter:</strong> Calendar-based calculation may differ ±1 day from official moon-sighting announcements by the Bangladesh National Moon Sighting Committee. For religious observances, follow the official announcement.</li>
                <li><strong>Faraid:</strong> Educational tool. Faraid has many edge cases (Awl, Radd, Hajb). For actual estate distribution, consult a qualified Mufti specializing in ilm al-fara'id and a lawyer familiar with the Bangladesh Muslim Family Laws Ordinance 1961. We are not liable for legal disputes arising from use of this calculator.</li>
                <li><strong>Qibla Direction:</strong> Computed using great-circle bearing. Phone compass accuracy varies. For mosque construction or precise observation, use surveyor's instruments.</li>
                <li><strong>All Islamic calculators:</strong> Mainstream Hanafi madhhab by default; other madhhab options where applicable. Religious differences of opinion exist on edge cases — consult your local scholar.</li>
              </ul>
            </>
          }
          bn={
            <>
              <h3 className="mt-2 font-semibold text-foreground">আর্থিক</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>কর ক্যালকুলেটর: প্রতিটি অর্থ আইনে হার বদলায়; NBR থেকে যাচাই করুন।</li>
                <li>EMI/ঋণ ব্যাংক হার: নির্দেশক মাত্র; ব্যাংক থেকে যাচাই করুন।</li>
                <li>কারেন্সি কনভার্টার: মিড-মার্কেট রেট, প্রকৃত লেনদেনের রেট নয়।</li>
                <li>কাস্টমস ডিউটি: HS কোড, FTA ও কাস্টমস মূল্যায়নের উপর নির্ভরশীল।</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">জমি</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>আঞ্চলিক ভিন্নতা থাকে; মৌজা রেকর্ড থেকে যাচাই করুন।</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">স্বাস্থ্য</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>স্বাস্থ্য ফলাফলের ভিত্তিতে কাজ করার আগে চিকিৎসকের পরামর্শ নিন।</li>
                <li>বডি ফ্যাট অনুমানে ±৩% ত্রুটি; ক্যালরি টার্গেট আনুমানিক।</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">শিক্ষাগত</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li>ম্যাথ সলভার শুধু শেখার জন্য — পরীক্ষায় ব্যবহারের জন্য নয়।</li>
                <li>Casio এমুলেটর প্র্যাকটিস টুল; প্রকৃত পরীক্ষায় ফিজিক্যাল ক্যালকুলেটর প্রয়োজন।</li>
              </ul>
              <h3 className="mt-3 font-semibold text-foreground">ইসলামিক</h3>
              <ul className="ml-5 list-disc space-y-1">
                <li><strong>যাকাত:</strong> অনুমানের জন্য। স্বর্ণ/রূপার মূল্য পরিবর্তনশীল — বর্তমান বাজার দরে যাচাই করুন। ব্যবসা, মিশ্র সম্পদ, কৃষি উশর, পশুসম্পদের মতো জটিল ক্ষেত্রে যোগ্য আলেমের পরামর্শ নিন।</li>
                <li><strong>নামাজের সময়:</strong> জ্যোতির্বৈজ্ঞানিক হিসাব (কারাচি পদ্ধতি ডিফল্ট)। স্থানীয় মসজিদের আজান সময় কিছুটা ভিন্ন হতে পারে। নিশ্চিতির জন্য স্থানীয় মসজিদ বা ইসলামিক ফাউন্ডেশন বাংলাদেশ থেকে যাচাই করুন।</li>
                <li><strong>সেহরি ও ইফতার:</strong> সময় পদ্ধতি ও শহরের উপর নির্ভরশীল। সর্বদা আজানের আগে সেহরি শেষ করুন এবং মাগরিবের আজানে ইফতার করুন।</li>
                <li><strong>হিজরি কনভার্টার:</strong> ক্যালেন্ডার-ভিত্তিক হিসাব বাংলাদেশ জাতীয় চাঁদ দেখা কমিটির অফিসিয়াল ঘোষণা থেকে ±১ দিন ভিন্ন হতে পারে। ধর্মীয় পালনের জন্য অফিসিয়াল ঘোষণা অনুসরণ করুন।</li>
                <li><strong>ফরায়েজ:</strong> শিক্ষাগত টুল। ফরায়েজে অনেক বিশেষ ক্ষেত্র (আউল, রদ্দ, হাজব) আছে। প্রকৃত সম্পত্তি বণ্টনের জন্য ইলম আল-ফারায়েজ-এ বিশেষজ্ঞ যোগ্য মুফতি ও বাংলাদেশ মুসলিম ফ্যামিলি ল’স অর্ডিন্যান্স ১৯৬১-এ পরিচিত আইনজীবীর পরামর্শ নিন। এই ক্যালকুলেটর ব্যবহার থেকে উদ্ভূত আইনি বিরোধের জন্য আমরা দায়ী নই।</li>
                <li><strong>কিবলা:</strong> গ্রেট-সার্কেল বেয়ারিং দ্বারা গণিত। ফোন কম্পাসের নির্ভুলতা পরিবর্তনশীল। মসজিদ নির্মাণ বা সূক্ষ্ম পর্যবেক্ষণের জন্য সার্ভেয়রের যন্ত্র ব্যবহার করুন।</li>
                <li><strong>সব ইসলামিক ক্যালকুলেটর:</strong> ডিফল্টে মূলধারার হানাফি মাযহাব; প্রযোজ্য ক্ষেত্রে অন্য মাযহাবের অপশন। বিশেষ ক্ষেত্রে মতপার্থক্য থাকতে পারে — স্থানীয় আলেমের পরামর্শ নিন।</li>
              </ul>
            </>
          }
        />
      </ContentSection>

      <ContentSection headingEn="4. Intellectual property" headingBn="৪. মেধাস্বত্ব">
        <BiText
          en={
            <p>
              Bangla Calculator content © 2026 — calculator implementations, design, and original content. Open data sources are cited where applicable
              (NBR, Bangladesh Bank, BBS, Islamic Foundation Bangladesh). Qur'anic verses are not copyrighted; Hadith references cite their source.
            </p>
          }
          bn={
            <p>
              বাংলা ক্যালকুলেটর কনটেন্ট © ২০২৬ — ক্যালকুলেটর বাস্তবায়ন, ডিজাইন ও মৌলিক কনটেন্ট। প্রযোজ্য ক্ষেত্রে উন্মুক্ত ডাটা উৎস উল্লেখ করা হয়েছে
              (NBR, বাংলাদেশ ব্যাংক, BBS, ইসলামিক ফাউন্ডেশন বাংলাদেশ)। কুরআনের আয়াত কপিরাইটমুক্ত; হাদিসের রেফারেন্সে উৎস উল্লেখ করা হয়েছে।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="5. User-generated content" headingBn="৫. ব্যবহারকারী কনটেন্ট">
        <BiText en={<p>None at present.</p>} bn={<p>বর্তমানে নেই।</p>} />
      </ContentSection>

      <ContentSection headingEn="6. Limitation of liability" headingBn="৬. দায়বদ্ধতার সীমাবদ্ধতা">
        <BiText
          en={<p>The site is provided as-is, with no warranty. We are not liable for decisions made based on calculator outputs — financial, health, legal, religious, or otherwise.</p>}
          bn={<p>সাইট কোনো ওয়ারেন্টি ছাড়া "যেমন আছে তেমন" দেওয়া। ক্যালকুলেটরের ফলাফলের ভিত্তিতে নেওয়া সিদ্ধান্তের জন্য — আর্থিক, স্বাস্থ্য, আইনি, ধর্মীয় বা অন্যান্য — আমরা দায়ী নই।</p>}
        />
      </ContentSection>

      <ContentSection headingEn="7. Religious authority disclaimer" headingBn="৭. ধর্মীয় কর্তৃত্ব দাবিত্যাগ">
        <BiText
          en={
            <p>
              Bangla Calculator is not a religious authority. Our Islamic calculators are educational tools that follow well-established mainstream rulings.
              For religious rulings (fatwa) on your specific situation, consult a qualified Mufti or your local scholar (Imam, Khateeb, Madrasah teacher).
            </p>
          }
          bn={
            <p>
              বাংলা ক্যালকুলেটর কোনো ধর্মীয় কর্তৃপক্ষ নয়। আমাদের ইসলামিক ক্যালকুলেটরগুলো সুপ্রতিষ্ঠিত মূলধারার রায় অনুসরণকারী শিক্ষাগত টুল।
              আপনার নির্দিষ্ট পরিস্থিতির ফাতওয়ার জন্য যোগ্য মুফতি বা আপনার স্থানীয় আলেম (ইমাম, খতিব, মাদ্রাসা শিক্ষক) এর পরামর্শ নিন।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="8. Changes to terms" headingBn="৮. শর্তাবলী পরিবর্তন">
        <BiText en={<p>We may update these terms; the latest version is always at /terms.</p>} bn={<p>আমরা শর্তাবলী আপডেট করতে পারি; সর্বশেষ সংস্করণ সর্বদা /terms-এ থাকবে।</p>} />
      </ContentSection>

      <ContentSection headingEn="9. Governing law" headingBn="৯. প্রযোজ্য আইন">
        <BiText en={<p>Bangladesh law governs these terms.</p>} bn={<p>এই শর্তাবলী বাংলাদেশের আইন দ্বারা পরিচালিত।</p>} />
      </ContentSection>

      <ContentSection headingEn="10. Contact" headingBn="১০. যোগাযোগ">
        <BiText
          en={<p>Legal queries: <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a>.</p>}
          bn={<p>আইনি প্রশ্ন: <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a>।</p>}
        />
      </ContentSection>
    </ContentPage>
  );
}
